const runWhenIdle = (fn, timeout = 2000) => {
  if ("requestIdleCallback" in window) {
    requestIdleCallback(fn, { timeout });
    return;
  }
  setTimeout(fn, 1);
};

const runWhenVisible = (selector, fn, rootMargin = "200px") => {
  const el = document.querySelector(selector);
  if (!el) return;
  if (!("IntersectionObserver" in window)) {
    fn();
    return;
  }
  const io = new IntersectionObserver((entries) => {
    if (!entries[0]?.isIntersecting) return;
    io.disconnect();
    fn();
  }, { rootMargin });
  io.observe(el);
};

function initMenu() {
  const headerEl = document.getElementById("siteHeader");
  const burgerEl = document.getElementById("burger");
  const menuEl = document.getElementById("menu");
  if (!headerEl || !burgerEl || !menuEl) return;

  burgerEl.addEventListener("click", () => {
    const open = headerEl.classList.toggle("open");
    burgerEl.setAttribute("aria-expanded", String(open));
  });

  menuEl.querySelectorAll("a").forEach((a) => {
    a.addEventListener("click", () => {
      headerEl.classList.remove("open");
      burgerEl.setAttribute("aria-expanded", "false");
    });
  });
}

function initBgLoader() {
  const blocks = document.querySelectorAll(".safe-bg[data-bg]");
  if (!blocks.length) return;

  const applyBackground = (el, url) => {
    if (!url) return;
    const img = new Image();
    img.decoding = "async";
    img.loading = "lazy";
    img.src = url;
    img.onload = () => {
      el.style.backgroundImage = `url('${url}')`;
      el.classList.add("is-loaded");
    };
  };

  const eagerBlocks = document.querySelectorAll(".about-image.safe-bg[data-bg]");
  eagerBlocks.forEach((el) => applyBackground(el, el.getAttribute("data-bg")));

  if (!("IntersectionObserver" in window)) {
    blocks.forEach((el) => {
      if (el.classList.contains("is-loaded")) return;
      applyBackground(el, el.getAttribute("data-bg"));
    });
    return;
  }

  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      applyBackground(el, el.getAttribute("data-bg"));
      io.unobserve(el);
    });
  }, { rootMargin: "300px" });

  blocks.forEach((el) => {
    if (el.classList.contains("is-loaded")) return;
    io.observe(el);
  });

  // Safety net: some mobile browsers may skip IO callback in edge cases.
  window.addEventListener("load", () => {
    document.querySelectorAll(".safe-bg[data-bg]").forEach((el) => {
      if (el.classList.contains("is-loaded")) return;
      applyBackground(el, el.getAttribute("data-bg"));
    });
  }, { once: true });
}

function initContactFloat() {
  const contactToggle = document.getElementById("contactToggle");
  const contactFloat = document.querySelector(".contact-float");
  const contactMenu = document.getElementById("contactMenu");
  if (!contactToggle || !contactFloat || !contactMenu) return;

  contactToggle.addEventListener("click", () => {
    const opened = contactFloat.classList.toggle("open");
    contactMenu.setAttribute("aria-hidden", opened ? "false" : "true");
  });

  document.addEventListener("click", (e) => {
    if (contactFloat.contains(e.target)) return;
    contactFloat.classList.remove("open");
    contactMenu.setAttribute("aria-hidden", "true");
  });
}

function initSlider() {
  const allSlides = Array.from(document.querySelectorAll(".slide"));
  let slides = [];
  const thumbsRoot = document.getElementById("gallery-thumbs");
  let thumbs = [];
  const slider = document.querySelector(".slider-wrapper");
  const scroller = slider?.querySelector(".slider-scroller");
  const track = scroller?.querySelector(".slider-track");
  if (!track || !scroller || !allSlides.length || !slider || !thumbsRoot) return;

  track.style.removeProperty("transform");
  track.style.removeProperty("transition");

  let index = 0;
  const sliderCaptionEl = document.getElementById("slider-caption");
  const isDesktop = () => window.matchMedia("(min-width: 1025px)").matches;
  /** ПК и планшет в альбоме — набор «горизонтальных» кадров; телефон/планшет в портрете — «вертикальных». */
  const preferPortraitShots = () => {
    if (isDesktop()) return false;
    return window.matchMedia("(orientation: portrait)").matches;
  };
  const mediaRules = {
    "first-desktop.jpg": { fit: "contain", pos: "center center" },
    "first-mobile.jpg": { fit: "cover", pos: "center 34%" },
    "069.jpg": { fit: "contain", pos: "center center" },
    "089.jpg": { fit: "contain", pos: "center center" },
    "155.jpg": { fit: "cover", pos: "center 34%" },
    "156.jpg": { fit: "contain", pos: "center center" },
    "163.jpg": { fit: "contain", pos: "center center" },
    "167.jpg": { fit: "contain", pos: "center center" },
    "168.jpg": { fit: "contain", pos: "center center" },
    "170.jpg": { fit: "cover", pos: "center 34%" },
    "173.jpg": { fit: "contain", pos: "center center" },
    "530.jpg": { fit: "cover", pos: "center 34%" },
    "583.jpg": { fit: "cover", pos: "center 34%" },
    "dsc_4979-web.jpg": { fit: "cover", pos: "center 36%" },
    "241024-037-web.jpg": { fit: "cover", pos: "center 35%" },
    "dsc_3724-web.jpg": { fit: "cover", pos: "center 34%" },
    "241024-049-web.jpg": { fit: "contain", pos: "center center" },
    "dsc_6089-web.jpg": { fit: "cover", pos: "center 35%" },
    "241024-056-web.jpg": { fit: "cover", pos: "center 34%" },
    "241024-058-web.jpg": { fit: "cover", pos: "center 34%" },
    "img_7145-web.jpg": { fit: "cover", pos: "center 34%" },
    "img_7146-web.jpg": { fit: "cover", pos: "center 34%" },
    "img_7147-web.jpg": { fit: "cover", pos: "center 34%" },
    "3-web.jpg": { fit: "cover", pos: "center 34%" },
    "img_6400-web.jpg": { fit: "cover", pos: "center 34%" },
    "2-web.jpg": { fit: "cover", pos: "center 34%" },
    "1-web.jpg": { fit: "cover", pos: "center 34%" }
  };
  const applySlideRule = (img, src) => {
    const fileName = (src.split("/").pop() || "").toLowerCase();
    const rule = mediaRules[fileName] || { fit: "cover", pos: "center 34%" };
    img.classList.toggle("fit-contain", rule.fit === "contain");
    img.style.objectPosition = rule.pos;
  };
  /** Видимые кадры совпадают с CSS (data-photo-orientation + media); display не трогаем — верстка в main.scss / _sections.scss. */
  const refreshVisibleSlides = () => {
    const wantPortrait = preferPortraitShots();
    slides = allSlides.filter((img) => {
      const wrap = img.closest(".slide-wrap");
      const ori = (wrap?.dataset.photoOrientation || "landscape").toLowerCase();
      if (ori === "both") return true;
      if (wantPortrait) return ori === "portrait";
      return ori === "landscape";
    });
    if (!slides.length) {
      slides = allSlides.slice();
      index = 0;
    } else {
      index = Math.min(index, slides.length - 1);
    }
  };

  const getActiveWrap = (i) => slides[i]?.closest(".slide-wrap");

  /** Сумма ширин видимых слайдов 0..i-1 — стабильнее scrollIntoView в Chrome. */
  const getScrollLeftForIndex = (i) => {
    let left = 0;
    const n = Math.min(i, slides.length);
    for (let j = 0; j < n; j++) {
      const wrap = getActiveWrap(j);
      if (wrap) left += wrap.offsetWidth;
    }
    return left;
  };

  const scrollGalleryToIndex = (i) => {
    const left = getScrollLeftForIndex(i);
    const max = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const clamped = Math.min(left, max);
    scroller.scrollTo({ left: clamped, behavior: "auto" });
    if (Math.abs(scroller.scrollLeft - clamped) > 1) {
      scroller.scrollLeft = clamped;
    }
  };

  const syncIndexFromScroll = () => {
    if (ignoreScroll || !slides.length) return;
    const sl = scroller.scrollLeft;
    const viewMid = sl + scroller.clientWidth * 0.5;
    let acc = 0;
    let bestI = 0;
    let bestDist = Infinity;
    for (let i = 0; i < slides.length; i++) {
      const wrap = getActiveWrap(i);
      const w = wrap?.offsetWidth || 0;
      const slideMid = acc + w * 0.5;
      const d = Math.abs(viewMid - slideMid);
      if (d < bestDist) {
        bestDist = d;
        bestI = i;
      }
      acc += w;
    }
    if (bestI !== index) {
      index = bestI;
      updateSliderUI();
    }
  };

  const pickSource = (img) => {
    const desktopSrc = img.dataset.srcDesktop;
    const mobileSrc = img.dataset.srcMobile;
    if (desktopSrc || mobileSrc) {
      return preferPortraitShots() ? (mobileSrc || desktopSrc) : (desktopSrc || mobileSrc);
    }
    return img.dataset.src || "";
  };
  const thumbFromSrc = (src) => {
    if (!src) return "";
    const normalized = src.replace(/\\/g, "/");
    return normalized.replace("img/gallery/", "img/gallery/thumbs/");
  };

  const buildThumbs = () => {
    thumbsRoot.innerHTML = "";
    slides.forEach((img, i) => {
      const thumb = document.createElement("img");
      thumb.src = thumbFromSrc(pickSource(img));
      thumb.alt = "";
      thumb.className = "thumb";
      thumb.width = 60;
      thumb.height = 40;
      thumb.loading = "lazy";
      thumb.addEventListener("click", () => showSlide(i));
      thumbsRoot.appendChild(thumb);
    });
    thumbs = Array.from(thumbsRoot.querySelectorAll(".thumb"));
  };

  const markSlideLoaded = (img) => {
    if (!img) return;
    img.classList.add("loaded");
  };

  const loadSlideImage = (img) => {
    if (!img) return;
    const pickedSrc = pickSource(img);
    if (!pickedSrc) return;
    applySlideRule(img, pickedSrc);
    if (!img.hasAttribute("loading")) img.loading = "lazy";
    img.decoding = "async";
    img.onload = () => markSlideLoaded(img);
    img.onerror = () => markSlideLoaded(img);
    img.src = pickedSrc;
    if (img.complete) markSlideLoaded(img);
  };

  const lazyObserver = "IntersectionObserver" in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const img = entry.target;
          loadSlideImage(img);
          lazyObserver.unobserve(img);
        });
      }, { rootMargin: "300px" })
    : null;

  refreshVisibleSlides();
  allSlides.forEach((img) => {
    if (lazyObserver) lazyObserver.observe(img);
    else loadSlideImage(img);
  });
  /* Первые кадры без ожидания IntersectionObserver — иначе на медленной сети серая «дыра» */
  slides.slice(0, Math.min(3, slides.length)).forEach((img) => loadSlideImage(img));

  /** Целая ширина в px — Chrome меньше «плывёт» scrollLeft между слайдами. */
  const applySlideWidths = () => {
    const w = Math.max(1, Math.floor(scroller.clientWidth));
    allSlides.forEach((img) => {
      const wrap = img.closest(".slide-wrap");
      if (!wrap) return;
      if (window.getComputedStyle(wrap).display === "none") {
        wrap.style.flex = "";
        wrap.style.width = "";
        wrap.style.minWidth = "";
        wrap.style.maxWidth = "";
        return;
      }
      wrap.style.flex = `0 0 ${w}px`;
      wrap.style.width = `${w}px`;
      wrap.style.minWidth = `${w}px`;
      wrap.style.maxWidth = `${w}px`;
    });
  };

  let ignoreScroll = false;
  const updateSliderUI = () => {
    thumbs.forEach((t) => t.classList.remove("active"));
    if (thumbs[index]) thumbs[index].classList.add("active");
    if (sliderCaptionEl && slides[index]) {
      sliderCaptionEl.textContent = slides[index].dataset.caption || "";
    }
  };

  const showSlide = (i) => {
    index = (i + slides.length) % slides.length;
    applySlideWidths();
    ignoreScroll = true;
    const run = () => {
      scrollGalleryToIndex(index);
      updateSliderUI();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          ignoreScroll = false;
        });
      });
    };
    requestAnimationFrame(run);
  };

  scroller.addEventListener("scroll", syncIndexFromScroll, { passive: true });

  slider.querySelector(".slider-btn.next")?.addEventListener("click", () => showSlide(index + 1));
  slider.querySelector(".slider-btn.prev")?.addEventListener("click", () => showSlide(index - 1));

  let resizeSlideTimer = 0;
  window.addEventListener("resize", () => {
    window.clearTimeout(resizeSlideTimer);
    resizeSlideTimer = window.setTimeout(() => {
      applySlideWidths();
      ignoreScroll = true;
      requestAnimationFrame(() => {
        scrollGalleryToIndex(index);
        updateSliderUI();
        requestAnimationFrame(() => {
          ignoreScroll = false;
        });
      });
    }, 80);
  }, { passive: true });

  const lightbox = document.getElementById("lightbox");
  const lightboxImg = document.getElementById("lightbox-img");
  const lightboxCaption = document.getElementById("lightbox-caption");

  const updateLightboxImage = () => {
    if (!lightboxImg || !slides[index]) return;
    const img = slides[index];
    lightboxImg.src = img.src || img.dataset.src || "";
    lightboxImg.alt = img.alt || "";
    if (lightboxCaption) lightboxCaption.textContent = img.dataset.caption || "";
  };

  const openLightbox = (i) => {
    if (!lightbox || !slides[i]) return;
    showSlide(i);
    updateLightboxImage();
    lightbox.hidden = false;
    lightbox.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
    lightbox.querySelector(".lightbox-close")?.focus();
  };

  const closeLightbox = () => {
    if (!lightbox) return;
    lightbox.hidden = true;
    lightbox.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
  };

  allSlides.forEach((img) => {
    img.closest(".slide-wrap")?.addEventListener("click", () => {
      const visibleIndex = slides.indexOf(img);
      if (visibleIndex === -1) return;
      openLightbox(visibleIndex);
    });
  });

  const lightboxPrev = () => { showSlide(index - 1); updateLightboxImage(); };
  const lightboxNext = () => { showSlide(index + 1); updateLightboxImage(); };

  lightbox?.querySelector(".lightbox-close")?.addEventListener("click", closeLightbox);
  lightbox?.querySelector(".lightbox-prev")?.addEventListener("click", lightboxPrev);
  lightbox?.querySelector(".lightbox-next")?.addEventListener("click", lightboxNext);
  lightbox?.addEventListener("click", (e) => { if (e.target === lightbox) closeLightbox(); });

  document.addEventListener("keydown", (e) => {
    if (lightbox?.getAttribute("aria-hidden") === "true") return;
    if (e.key === "Escape") closeLightbox();
    if (e.key === "ArrowLeft") lightboxPrev();
    if (e.key === "ArrowRight") lightboxNext();
  });

  const canAutoSlide = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  if (canAutoSlide) {
    let autoSlide = 0;
    const startAuto = () => {
      autoSlide = window.setInterval(() => showSlide(index + 1), 5000);
    };
    const stopAuto = () => {
      window.clearInterval(autoSlide);
    };
    slider.addEventListener("mouseenter", stopAuto);
    slider.addEventListener("mouseleave", startAuto);
    startAuto();
  }

  const onViewportSwitch = () => {
    refreshVisibleSlides();
    buildThumbs();
    allSlides.forEach((img) => {
      img.removeAttribute("src");
      img.classList.remove("loaded");
      img.classList.remove("fit-contain");
      loadSlideImage(img);
    });
    applySlideWidths();
    showSlide(index);
  };
  window.matchMedia("(min-width: 1025px)").addEventListener("change", onViewportSwitch);
  window.matchMedia("(orientation: portrait)").addEventListener("change", onViewportSwitch);

  buildThumbs();
  applySlideWidths();
  showSlide(0);

  let galleryLayoutTimer = 0;
  const layoutGalleryAfterResize = () => {
    window.clearTimeout(galleryLayoutTimer);
    galleryLayoutTimer = window.setTimeout(() => {
      applySlideWidths();
      ignoreScroll = true;
      requestAnimationFrame(() => {
        scrollGalleryToIndex(index);
        requestAnimationFrame(() => {
          ignoreScroll = false;
        });
      });
    }, 60);
  };
  if ("ResizeObserver" in window) {
    new ResizeObserver(layoutGalleryAfterResize).observe(scroller);
  }
  window.visualViewport?.addEventListener("resize", layoutGalleryAfterResize, { passive: true });
}

function initScrollSpy() {
  if (!("IntersectionObserver" in window)) return;
  const sections = [
    { id: "about", section: "about" },
    { id: "gallery", section: "gallery" },
    { id: "why", section: "why" },
    { id: "faq", section: "faq" },
    { id: "contact", section: "contact" }
  ];
  const navLinks = document.querySelectorAll("a[data-section]");
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const section = sections.find((s) => s.id === entry.target.id)?.section;
      if (!section) return;
      navLinks.forEach((a) => {
        const active = a.getAttribute("data-section") === section;
        a.classList.toggle("active", active);
        if (active) a.setAttribute("aria-current", "true");
        else a.removeAttribute("aria-current");
      });
    });
  }, { rootMargin: "-20% 0px -60% 0px", threshold: 0 });

  sections.forEach(({ id }) => {
    const el = document.getElementById(id);
    if (el) observer.observe(el);
  });
}

function initFaq() {
  document.querySelectorAll("[data-faq-toggle]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const expanded = btn.getAttribute("aria-expanded") === "true";
      const answer = document.getElementById(btn.getAttribute("aria-controls"));
      btn.setAttribute("aria-expanded", String(!expanded));
      if (answer) answer.hidden = expanded;
    });
  });
}

function initUpcomingEventsVisibility() {
  const upcomingSection = document.getElementById("upcoming");
  if (!upcomingSection) return;

  const now = new Date();
  const cards = Array.from(upcomingSection.querySelectorAll(".upcoming-card"));

  let visibleCardsCount = 0;

  cards.forEach((card) => {
    const dateEl = card.querySelector("time[datetime]");
    const isoDate = dateEl?.getAttribute("datetime");
    if (!isoDate) {
      visibleCardsCount += 1;
      return;
    }

    const eventDate = new Date(`${isoDate}T23:59:59`);
    const isValidDate = !Number.isNaN(eventDate.getTime());
    const isPast = isValidDate && eventDate < now;

    if (isPast) {
      card.hidden = true;
      card.setAttribute("aria-hidden", "true");
      return;
    }

    visibleCardsCount += 1;
  });

  if (visibleCardsCount === 0) {
    upcomingSection.hidden = true;
    upcomingSection.setAttribute("aria-hidden", "true");
  }
}

function initReviewsCarousel() {
  const root = document.querySelector("[data-reviews-slider]");
  if (!root) return;

  const track = root.querySelector("[data-reviews-track]");
  const cards = Array.from(root.querySelectorAll(".review-card"));
  const prevBtn = root.querySelector("[data-reviews-prev]");
  const nextBtn = root.querySelector("[data-reviews-next]");
  const dotsWrap = document.querySelector("[data-reviews-dots]");
  if (!track || !cards.length || !dotsWrap) return;

  let index = 0;
  let visible = 1;
  let maxIndex = 0;
  let autoTimer = 0;

  const getGap = () => {
    const styles = window.getComputedStyle(track);
    return Number.parseFloat(styles.columnGap || styles.gap || "0") || 0;
  };

  const recalc = () => {
    const firstCard = cards[0];
    if (!firstCard) return;
    const cardWidth = firstCard.getBoundingClientRect().width;
    const wrapWidth = track.parentElement.getBoundingClientRect().width;
    const gap = getGap();
    visible = Math.max(1, Math.floor((wrapWidth + gap) / (cardWidth + gap)));
    maxIndex = Math.max(0, cards.length - visible);
    if (index > maxIndex) index = maxIndex;
  };

  const renderDots = () => {
    const pages = maxIndex + 1;
    dotsWrap.innerHTML = "";
    for (let i = 0; i < pages; i += 1) {
      const btn = document.createElement("button");
      btn.className = "reviews-dot";
      btn.type = "button";
      btn.setAttribute("aria-label", `Показать отзывы ${i + 1}`);
      btn.addEventListener("click", () => goTo(i));
      dotsWrap.appendChild(btn);
    }
  };

  const updateUI = () => {
    const isMobile = window.matchMedia("(max-width: 760px)").matches;
    if (isMobile) {
      track.style.transform = "";
      return;
    }
    const firstCard = cards[0];
    if (!firstCard) return;
    const shift = index * (firstCard.getBoundingClientRect().width + getGap());
    track.style.transform = `translateX(-${shift}px)`;
    if (prevBtn) prevBtn.disabled = index <= 0;
    if (nextBtn) nextBtn.disabled = index >= maxIndex;
    Array.from(dotsWrap.children).forEach((dot, dotIndex) => {
      dot.classList.toggle("active", dotIndex === index);
    });
  };

  const goTo = (nextIndex) => {
    index = Math.min(maxIndex, Math.max(0, nextIndex));
    updateUI();
  };

  const restartAuto = () => {
    window.clearInterval(autoTimer);
    const isMobile = window.matchMedia("(max-width: 760px)").matches;
    if (isMobile || maxIndex === 0) return;
    autoTimer = window.setInterval(() => {
      goTo(index >= maxIndex ? 0 : index + 1);
    }, 5200);
  };

  prevBtn?.addEventListener("click", () => {
    goTo(index - 1);
    restartAuto();
  });
  nextBtn?.addEventListener("click", () => {
    goTo(index + 1);
    restartAuto();
  });

  root.addEventListener("mouseenter", () => window.clearInterval(autoTimer));
  root.addEventListener("mouseleave", restartAuto);
  window.addEventListener("resize", () => {
    recalc();
    renderDots();
    updateUI();
    restartAuto();
  }, { passive: true });

  recalc();
  renderDots();
  updateUI();
  restartAuto();
}

function initParallax() {
  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (prefersReducedMotion) return;

  const items = Array.from(document.querySelectorAll("[data-parallax-speed]"));
  if (!items.length) return;

  const isCoarsePointer = window.matchMedia("(pointer: coarse)").matches;
  const maxOffset = isCoarsePointer ? 20 : 42;
  const visibleItems = new Set();
  let ticking = false;

  const applyTransform = () => {
    const viewportHalf = window.innerHeight / 2;

    visibleItems.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const elCenter = rect.top + rect.height / 2;
      const progress = (elCenter - viewportHalf) / viewportHalf;

      const speed = Number.parseFloat(el.dataset.parallaxSpeed || "0.4");
      const axis = el.dataset.parallaxAxis === "x" ? "x" : "y";
      const scale = Number.parseFloat(el.dataset.parallaxScale || "1");
      const offset = Math.max(-maxOffset, Math.min(maxOffset, progress * maxOffset * speed));

      if (axis === "x") {
        el.style.transform = `translate3d(${offset}px, 0, 0) scale(${scale})`;
      } else {
        el.style.transform = `translate3d(0, ${offset}px, 0) scale(${scale})`;
      }
    });

    ticking = false;
  };

  const requestTick = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(applyTransform);
  };

  if ("IntersectionObserver" in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) visibleItems.add(entry.target);
        else visibleItems.delete(entry.target);
      });
      requestTick();
    }, { rootMargin: "220px" });
    items.forEach((el) => io.observe(el));
  } else {
    items.forEach((el) => visibleItems.add(el));
  }

  window.addEventListener("scroll", requestTick, { passive: true });
  window.addEventListener("resize", requestTick, { passive: true });
  requestTick();
}

function initCallbackForm() {
  const form = document.getElementById("callbackForm");
  if (!form) return;

  const phoneInput = document.getElementById("callbackPhone");
  const successEl = document.getElementById("callbackSuccess");
  const errorEl = document.getElementById("callbackError");

  const showStatus = (ok) => {
    if (!successEl || !errorEl) return;
    successEl.hidden = !ok;
    errorEl.hidden = ok;
  };

  const formatPhone = (raw) => {
    const digits = raw.replace(/\D/g, "");
    const d = digits.startsWith("7") ? digits : `7${digits.slice(-10)}`;
    const p = d.slice(0, 11);
    const a = p.slice(1, 4);
    const b = p.slice(4, 7);
    const c = p.slice(7, 9);
    const e = p.slice(9, 11);
    return `+7${a ? ` (${a}` : ""}${a.length === 3 ? ")" : ""}${b ? ` ${b}` : ""}${c ? `-${c}` : ""}${e ? `-${e}` : ""}`;
  };

  phoneInput?.addEventListener("input", () => {
    phoneInput.value = formatPhone(phoneInput.value);
  });

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    showStatus(false);

    if (form.website && form.website.value) return; // honeypot
    if (!form.reportValidity()) return;

    const submitBtn = form.querySelector("button[type='submit']");
    submitBtn?.setAttribute("disabled", "disabled");

    try {
      const fd = new FormData(form);
      const response = await fetch("send-callback.php", {
        method: "POST",
        body: fd
      });
      const json = await response.json();
      if (!response.ok || !json.ok) throw new Error(json.error || "send_failed");
      form.reset();
      showStatus(true);
    } catch (_) {
      showStatus(false);
      if (errorEl) errorEl.hidden = false;
    } finally {
      submitBtn?.removeAttribute("disabled");
    }
  });
}

function initTheme() {
  const htmlRoot = document.getElementById("html-root");
  const themeToggle = document.getElementById("themeToggle");
  const THEME_KEY = "borch-theme";
  if (!htmlRoot) return;

  const setTheme = (theme) => {
    htmlRoot.setAttribute("data-theme", theme);
    if (themeToggle) {
      themeToggle.setAttribute("aria-label", theme === "light"
        ? "Переключить тему: тёмная"
        : "Переключить тему: светлая");
    }
    try { localStorage.setItem(THEME_KEY, theme); } catch (_) {}
  };

  let saved = null;
  try { saved = localStorage.getItem(THEME_KEY); } catch (_) {}
  if (saved === "light") setTheme("light");

  themeToggle?.addEventListener("click", () => {
    const current = htmlRoot.getAttribute("data-theme") || "dark";
    setTheme(current === "dark" ? "light" : "dark");
  });
}

function initReveal() {
  if (!("IntersectionObserver" in window)) return;
  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (prefersReducedMotion) return;

  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) entry.target.classList.add("revealed");
    });
  }, { rootMargin: "0px 0px -60px 0px", threshold: 0 });

  document.querySelectorAll("section").forEach((section) => {
    section.classList.add("reveal");
    revealObserver.observe(section);
  });
}

initMenu();
initTheme();
initFaq();
initUpcomingEventsVisibility();
initBgLoader();

runWhenVisible("#gallery", () => runWhenIdle(initSlider, 1500), "300px");
runWhenVisible("#reviews", () => runWhenIdle(initReviewsCarousel, 1200), "250px");
runWhenIdle(initParallax, 900);
runWhenIdle(initCallbackForm, 900);
runWhenIdle(initContactFloat, 1200);
runWhenIdle(initScrollSpy, 1200);
runWhenIdle(initReveal, 1500);
